import {
  Box,
  Flex,
  Text,
  Collapse,
  useDisclosure,
  Icon,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

const HowItWorksBanner = () => {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? true;

  const bg = useColorModeValue("rgba(15,23,42,0.04)", "rgba(255,255,255,0.04)");
  const cardBg = useColorModeValue("white", "rgba(18, 18, 20, 0.85)");
  const textMain = useColorModeValue("gray.900", "whiteAlpha.900");
  const textSub = useColorModeValue("gray.600", "whiteAlpha.600");
  const connectorColor = useColorModeValue("gray.400", "whiteAlpha.300");
  const dropdownTextMain = useColorModeValue("gray.800", "whiteAlpha.800");
  const dropdownTextSub = useColorModeValue("gray.600", "whiteAlpha.600");
  const hoverBg = useColorModeValue("gray.100", "rgba(255,255,255,0.06)");
  const outerBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const cardShadow = useColorModeValue("sm", "0 8px 32px rgba(0,0,0,0.35)");
  const cardHoverShadow = useColorModeValue(
    "md",
    "0 12px 40px rgba(52,211,153,0.12), 0 16px 44px rgba(253,224,71,0.06)"
  );
  const stepCircleBorder = useColorModeValue("white", "whiteAlpha.200");
  const rowBg = useColorModeValue("white", "rgba(255,255,255,0.04)");

  const steps = [
    {
      label: t("howItWorks.step1Label"),
      number: 1,
      desc: t("howItWorks.step1Desc"),
      shortDesc: t("howItWorks.step1Short"),
      color: "brand.400",
    },
    {
      label: t("howItWorks.step2Label"),
      number: 2,
      desc: t("howItWorks.step2Desc"),
      shortDesc: t("howItWorks.step2Short"),
      color: "brand.300",
    },
    {
      label: t("howItWorks.step3Label"),
      number: 3,
      desc: t("howItWorks.step3Desc"),
      shortDesc: t("howItWorks.step3Short"),
      color: "brand.500",
    },
  ];

  return (
    <Box
      w="100%"
      bg={bg}
      p={{ base: 1.5, md: 2 }}
      borderRadius="2xl"
      border="1px solid"
      borderColor={outerBorder}
      backdropFilter="blur(12px)"
    >
      <Flex
        as="button"
        type="button"
        onClick={onToggle}
        w="100%"
        align="center"
        justify="space-between"
        px={2}
        py={2}
        borderRadius="lg"
        _hover={{ bg: hoverBg }}
        transition="background-color 0.2s"
      >
        <Text fontSize={{ base: "sm", lg: "md" }} fontWeight="bold" color={dropdownTextMain}>
          {t("howItWorks.title")}
        </Text>
        <Icon as={isOpen ? ChevronUpIcon : ChevronDownIcon} w={5} h={5} color={dropdownTextMain} />
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Box mt={1} borderRadius="xl" px={{ base: 1, md: 4 }} pb={{ base: 2, md: 4 }} pt={1}>
          {isMobile ? (
            <Flex direction="column" gap={2.5}>
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="extrabold"
                  lineHeight="1.25"
                  color={dropdownTextMain}
                  letterSpacing="-0.02em"
                >
                  {t("howItWorks.headlineLead")}{" "}
                  <Box as="span" bgGradient="linear(to-r, brand.300, brand.500)" bgClip="text">
                    {t("howItWorks.headlineRwandan")} {t("howItWorks.headlineDiaspora")}
                  </Box>{" "}
                  <Box as="span" bgGradient="linear(to-r, brand.400, brand.200)" bgClip="text">
                    {t("howItWorks.headlineWorldwide")}
                  </Box>
                </Text>
                <Text color={dropdownTextSub} fontSize="xs" mt={1.5} lineHeight="1.4">
                  {t("howItWorks.bodyShort")}
                </Text>
              </Box>

              <Flex direction="column" gap={1.5}>
                {steps.map((step) => (
                  <Flex
                    key={step.number}
                    align="center"
                    gap={3}
                    bg={rowBg}
                    border="1px solid"
                    borderColor={cardBorder}
                    borderRadius="xl"
                    px={3}
                    py={2.5}
                    minH="52px"
                  >
                    <Flex
                      bg={step.color}
                      color="surface.900"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      w="28px"
                      h="28px"
                      flexShrink={0}
                      fontWeight="bold"
                      fontSize="sm"
                      border="2px solid"
                      borderColor={stepCircleBorder}
                    >
                      {step.number}
                    </Flex>
                    <Box minW={0} flex={1}>
                      <Text fontSize="sm" fontWeight="bold" color={textMain} lineHeight="1.2">
                        {step.label}
                      </Text>
                      <Text fontSize="xs" color={textSub} lineHeight="1.35" noOfLines={2} mt={0.5}>
                        {step.shortDesc}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          ) : (
            <Flex direction="row" align="center" justify="space-between" maxW="1200px" mx="auto" gap={4}>
              <Box flex="1">
                <Text as="h2" fontSize={{ md: "md", lg: "lg" }} fontWeight="extrabold" lineHeight="1.1" color={dropdownTextMain}>
                  {t("howItWorks.headlineLead")} <br />
                  <Box as="span" bgGradient="linear(to-r, brand.300, brand.500)" bgClip="text">
                    {t("howItWorks.headlineRwandan")}{" "}
                    <Box as="span" fontStyle="italic" bgGradient="linear(to-r, brand.300, brand.500)" bgClip="text">
                      {t("howItWorks.headlineDiaspora")}
                    </Box>
                  </Box>
                  <br />
                  <Box as="span" bgGradient="linear(to-r, brand.400, brand.200)" bgClip="text">
                    {t("howItWorks.headlineWorldwide")}
                  </Box>
                </Text>
                <Text color={dropdownTextSub} fontSize="xs" maxW="220px" mt={5} lineHeight="tall">
                  {t("howItWorks.body")}
                </Text>
              </Box>
              <Flex flex="2" direction="row" justify="center" align="stretch" w="100%">
                {steps.map((step, idx) => (
                  <Flex key={step.number} direction="row" align="center" flex="1">
                    <Box
                      bg={cardBg}
                      borderRadius="xl"
                      boxShadow={cardShadow}
                      border="1px solid"
                      borderColor={cardBorder}
                      px={2}
                      py={2}
                      minW="120px"
                      maxW="220px"
                      flex="1"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      transition="box-shadow 0.2s, transform 0.2s"
                      _hover={{ boxShadow: cardHoverShadow, transform: "translateY(-2px)" }}
                      mx={2}
                    >
                      <Box
                        bg={step.color}
                        color="surface.900"
                        borderRadius="full"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="28px"
                        h="28px"
                        fontWeight="bold"
                        fontSize="md"
                        boxShadow="0 0 20px rgba(52,211,153,0.28), 0 0 36px rgba(253,224,71,0.1)"
                        border="2px solid"
                        borderColor={stepCircleBorder}
                        zIndex={2}
                      >
                        {step.number}
                      </Box>
                      <Text fontSize="sm" fontWeight="bold" color={textMain} mb={0.5} textAlign="center">
                        {step.label}
                      </Text>
                      <Text fontSize="2xs" color={textSub} textAlign="center" minH="40px">
                        {step.desc}
                      </Text>
                    </Box>
                    {idx < steps.length - 1 && (
                      <Box
                        h="0"
                        flexShrink={0}
                        width="32px"
                        borderBottom="2px dotted"
                        borderColor={connectorColor}
                        mx={1}
                        zIndex={1}
                      />
                    )}
                  </Flex>
                ))}
              </Flex>
            </Flex>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default HowItWorksBanner;
